import { supabase } from './supabaseClient';
import type { RaciAssignment, RaciRole } from '../types';

export const raciService = {
  // Buscar todas as atribuições RACI do projeto
  async getByProject(projectId: string): Promise<RaciAssignment[]> {
    const { data, error } = await supabase
      .from('raci_assignments')
      .select('*')
      .eq('project_id', projectId);

    if (error) {
      if (error.message?.includes('AbortError')) {
        console.log('[raciService] getByProject AbortError ignorado');
        return [];
      }
      throw error;
    }
    return data as RaciAssignment[];
  },

  // Criar ou atualizar uma atribuição RACI
  async setAssignment({ project_id, task_id, team_member_id, raci_role }: { project_id: string; task_id: string; team_member_id: string; raci_role: RaciRole }): Promise<RaciAssignment> {
    const { data, error } = await supabase
      .from('raci_assignments')
      .upsert({
        project_id,
        task_id,
        team_member_id,
        raci_role
      }, {
        onConflict: 'task_id,team_member_id,raci_role'
      })
      .select()
      .single();

    if (error) throw error;
    return data as RaciAssignment;
  },

  // Remover uma atribuição específica
  async removeAssignment(taskId: string, memberId: string, role: RaciRole): Promise<boolean> {
    const { error } = await supabase
      .from('raci_assignments')
      .delete()
      .eq('task_id', taskId)
      .eq('team_member_id', memberId)
      .eq('raci_role', role);

    if (error) throw error;
    return true;
  },

  // Remover todas as atribuições de um papel para uma tarefa
  // (usado para substituir Accountable)
  async removeRoleForTask(taskId: string, role: RaciRole): Promise<boolean> {
    const { error } = await supabase
      .from('raci_assignments')
      .delete()
      .eq('task_id', taskId)
      .eq('raci_role', role);

    if (error) throw error;
    return true;
  },

  // Remover todas as atribuições de um membro para uma tarefa
  async removeAllForMemberOnTask(taskId: string, memberId: string): Promise<boolean> {
    const { error } = await supabase
      .from('raci_assignments')
      .delete()
      .eq('task_id', taskId)
      .eq('team_member_id', memberId);

    if (error) throw error;
    return true;
  },

  /**
   * Agrega o RACI por PESSOA do projeto (letras + escopo por letra), para os
   * chips da tela Equipe & Alocação. Extraído da lógica que vivia presa em
   * RaciSummary.tsx. Escopo = títulos das tarefas onde a pessoa tem a letra.
   */
  async getMemberRoleSummary(projectId: string): Promise<Map<string, { roles: RaciRole[]; scopeByRole: Partial<Record<RaciRole, string[]>> }>> {
    const { data, error } = await supabase
      .from('raci_assignments')
      .select('team_member_id, raci_role, tasks(name)')
      .eq('project_id', projectId);

    if (error) {
      if (error.message?.includes('AbortError')) return new Map();
      throw error;
    }

    const ORDER: RaciRole[] = ['R', 'A', 'C', 'I'];
    const out = new Map<string, { roles: RaciRole[]; scopeByRole: Partial<Record<RaciRole, string[]>> }>();

    (data || []).forEach(row => {
      const r = row as unknown as { team_member_id: string; raci_role: RaciRole; tasks: { name: string } | null };
      const entry = out.get(r.team_member_id) || { roles: [], scopeByRole: {} };
      if (!entry.roles.includes(r.raci_role)) entry.roles.push(r.raci_role);
      const scope = entry.scopeByRole[r.raci_role] || [];
      const title = r.tasks?.name;
      if (title && !scope.includes(title)) scope.push(title);
      entry.scopeByRole[r.raci_role] = scope;
      out.set(r.team_member_id, entry);
    });

    out.forEach(entry => {
      entry.roles.sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
    });
    return out;
  },

  // Atribuição em lote
  async bulkSet(assignments: Partial<RaciAssignment>[]): Promise<RaciAssignment[]> {
    const { data, error } = await supabase
      .from('raci_assignments')
      .upsert(assignments, {
        onConflict: 'task_id,team_member_id,raci_role'
      })
      .select();

    if (error) throw error;
    return data as RaciAssignment[];
  }
};
